import { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import { Upload, Save, FileSpreadsheet } from 'lucide-react';

const HEADER_FIELDS = [
  { key: 'colleagueName', label: 'Colleague Name', hint: 'e.g. I3' },
  { key: 'colleaguePosition', label: 'Colleague Position', hint: 'e.g. Y3' },
  { key: 'officeLocation', label: 'Office Location', hint: 'e.g. I4' },
  { key: 'colleagueId', label: 'Colleague ID#', hint: 'e.g. Y4' },
];

const LEAVE_DETAIL_FIELDS = [
  { key: 'returnDate', label: 'Date Reporting Back', hint: 'e.g. I17' },
  { key: 'comments', label: 'Comments', hint: 'e.g. S17' },
];

const SUMMARY_FIELDS = [
  { key: 'alDays', label: 'Annual Leave (AL)', hint: 'e.g. C10' },
  { key: 'doDays', label: 'Day Off (DO)', hint: 'e.g. E10' },
  { key: 'ldDays', label: 'Lieu Day (LD)', hint: 'e.g. G10' },
  { key: 'phDays', label: 'Public Holiday (PH)', hint: 'e.g. I10' },
  { key: 'slDays', label: 'Sick Leave (SL)', hint: 'e.g. K10' },
  { key: 'mlDays', label: 'Maternity Leave (ML)', hint: 'e.g. M10' },
  { key: 'ulDays', label: 'Unpaid Leave (UL)', hint: 'e.g. O10' },
  { key: 'blDays', label: 'Business Leave (BL)', hint: 'e.g. Q10' },
  { key: 'clDays', label: 'Compassion Leave (CL)', hint: 'e.g. S10' },
  { key: 'plDays', label: 'Paternity Leave (PL)', hint: 'e.g. V10' },
  { key: 'hlDays', label: 'Hajj Leave (HL)', hint: 'e.g. X10' },
  { key: 'wlDays', label: 'Marriage Leave (WL)', hint: 'e.g. Z10' },
  { key: 'totalDays', label: 'Total Leave', hint: 'e.g. AC10' },
];

const SIGNATURE_FIELDS = [
  { key: 'colleagueSignName', label: 'Colleague Name', hint: 'e.g. I20' },
  { key: 'colleagueSignDate', label: 'Colleague Date', hint: 'e.g. X20' },
  { key: 'lineManagerName', label: 'Line Manager Name', hint: 'e.g. I21' },
  { key: 'lineManagerDate', label: 'Line Manager Date', hint: 'e.g. X21' },
];

export default function LeaveTemplatePage() {
  const toast = useToast();
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [cellMappings, setCellMappings] = useState({});
  const [calendarStartRow, setCalendarStartRow] = useState(13);
  const [calendarMonthCol, setCalendarMonthCol] = useState('A');
  const [calendarStartCol, setCalendarStartCol] = useState('B');
  const [calendarEndCol, setCalendarEndCol] = useState('AF');

  useEffect(() => {
    adminApi.getLeaveTemplateMapping()
      .then((res) => {
        setFileName(res.templateFileName || '');
        if (res.mapping) {
          const cm = res.mapping.cellMappings || {};
          const reversed = {};
          for (const [ref, field] of Object.entries(cm)) reversed[field] = ref;
          setCellMappings(reversed);
          setCalendarStartRow(res.mapping.calendarStartRow || 13);
          setCalendarMonthCol(res.mapping.calendarMonthCol || 'A');
          setCalendarStartCol(res.mapping.calendarStartCol || 'B');
          setCalendarEndCol(res.mapping.calendarEndCol || 'AF');
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('template', file);
      await adminApi.uploadLeaveTemplate(formData);
      setFileName(file.name);
      toast.success('Template uploaded');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cm = {};
      for (const [field, ref] of Object.entries(cellMappings)) {
        if (ref?.trim()) cm[ref.trim().toUpperCase()] = field;
      }

      await adminApi.saveLeaveTemplateMapping({
        mapping: {
          cellMappings: cm,
          calendarStartRow,
          calendarMonthCol: calendarMonthCol.toUpperCase(),
          calendarStartCol: calendarStartCol.toUpperCase(),
          calendarEndCol: calendarEndCol.toUpperCase(),
        },
      });
      toast.success('Mapping saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><div className="h-6 w-6 animate-spin rounded-full border-[2.5px] border-border border-t-primary" /></div>;
  }

  return (
    <div className="max-w-2xl">
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-primary" />
              Template File
            </CardTitle>
            <CardDescription>{fileName ? `Current: ${fileName}` : 'Upload your company XLSX leave request template'}</CardDescription>
          </CardHeader>
          <CardContent>
            <label className="flex items-center gap-3 rounded-lg border border-dashed border-border px-4 py-4 cursor-pointer hover:border-primary/30 hover:bg-muted/30 transition-all">
              <Upload className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-foreground">{uploading ? 'Uploading...' : 'Upload XLSX template'}</p>
                <p className="text-xs text-muted-foreground">Replaces current template</p>
              </div>
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} disabled={uploading} />
            </label>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Colleague Info</CardTitle>
            <CardDescription>Map the employee detail cells (top-left cell of merged range)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {HEADER_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-36 shrink-0">{f.label}</span>
                  <input
                    value={cellMappings[f.key] || ''}
                    onChange={(e) => setCellMappings((m) => ({ ...m, [f.key]: e.target.value }))}
                    placeholder={f.hint}
                    className="w-20 bg-card border border-border rounded-md px-2.5 py-1.5 text-xs font-mono uppercase text-center focus:border-primary/40 focus:outline-none transition-colors placeholder:text-muted-foreground/30 placeholder:normal-case"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Leave Summary (# of Days row)</CardTitle>
            <CardDescription>Map each leave type's day count to the top-left cell of its merged range in the summary row</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {SUMMARY_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-36 shrink-0">{f.label}</span>
                  <input
                    value={cellMappings[f.key] || ''}
                    onChange={(e) => setCellMappings((m) => ({ ...m, [f.key]: e.target.value }))}
                    placeholder={f.hint}
                    className="w-20 bg-card border border-border rounded-md px-2.5 py-1.5 text-xs font-mono uppercase text-center focus:border-primary/40 focus:outline-none transition-colors placeholder:text-muted-foreground/30 placeholder:normal-case"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Monthly Calendar</CardTitle>
            <CardDescription>
              The calendar row where day codes (AL, DO, SL, etc.) are filled in.
              Columns map to days 1–31 sequentially from Start Col to End Col.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-3 flex-wrap">
              <Input label="Data Row" type="number" min="1" value={calendarStartRow} onChange={(e) => setCalendarStartRow(parseInt(e.target.value) || 13)} className="w-24" />
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Month Col</label>
                <input value={calendarMonthCol} onChange={(e) => setCalendarMonthCol(e.target.value)} placeholder="A"
                  className="w-16 bg-card border border-border rounded-md px-2.5 py-1.5 text-xs font-mono uppercase text-center focus:border-primary/40 focus:outline-none transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Start Col (Day 1)</label>
                <input value={calendarStartCol} onChange={(e) => setCalendarStartCol(e.target.value)} placeholder="B"
                  className="w-16 bg-card border border-border rounded-md px-2.5 py-1.5 text-xs font-mono uppercase text-center focus:border-primary/40 focus:outline-none transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">End Col (Day 31)</label>
                <input value={calendarEndCol} onChange={(e) => setCalendarEndCol(e.target.value)} placeholder="AF"
                  className="w-16 bg-card border border-border rounded-md px-2.5 py-1.5 text-xs font-mono uppercase text-center focus:border-primary/40 focus:outline-none transition-colors" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Days 1–31 will fill sequentially across B→AF (or your configured range). Leave days get the type abbreviation (AL, SL, etc.).</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
            <CardDescription>Return date, comments, etc.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {LEAVE_DETAIL_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-36 shrink-0">{f.label}</span>
                  <input
                    value={cellMappings[f.key] || ''}
                    onChange={(e) => setCellMappings((m) => ({ ...m, [f.key]: e.target.value }))}
                    placeholder={f.hint}
                    className="w-20 bg-card border border-border rounded-md px-2.5 py-1.5 text-xs font-mono uppercase text-center focus:border-primary/40 focus:outline-none transition-colors placeholder:text-muted-foreground/30 placeholder:normal-case"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Signatures</CardTitle>
            <CardDescription>Map the signature block cells</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {SIGNATURE_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-36 shrink-0">{f.label}</span>
                  <input
                    value={cellMappings[f.key] || ''}
                    onChange={(e) => setCellMappings((m) => ({ ...m, [f.key]: e.target.value }))}
                    placeholder={f.hint}
                    className="w-20 bg-card border border-border rounded-md px-2.5 py-1.5 text-xs font-mono uppercase text-center focus:border-primary/40 focus:outline-none transition-colors placeholder:text-muted-foreground/30 placeholder:normal-case"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} loading={saving} disabled={saving}>
            <Save className="h-4 w-4" /> Save Mapping
          </Button>
        </div>
      </div>
    </div>
  );
}
